import { Link } from 'react-router-dom'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { Calendar } from 'lucide-react'

import {
    DataTableColumn,
    FilterItemConfig,
    FilterToggle,
} from '@/components/ui/data-table-customized'
import { Badge } from '@/components/ui/badge'
import { Item } from '../../types/response'
import { paths } from '@/utils/path'
import {
    getLocations,
    getOwners,
    getRacks,
    getSuppliers,
    getItemModelsPaginated,
} from '../../services'
import {
    EQUIPMENT_STATUS_VARIANTS,
    ITEM_CONDITION_VARIANTS,
    ITEM_TYPE,
} from '../../constants'

dayjs.extend(relativeTime)

// === Columns ===

export const getItemColumns = (): DataTableColumn<Item>[] => [
    {
        key: 'item_type',
        title: 'Type',
        width: 130,
        render: (_value, row) => {
            const variant =
                row.item_type === ITEM_TYPE.EQUIPMENT ? 'default' : 'secondary'
            return (
                <Badge variant={variant} className="capitalize">
                    {row.item_type}
                </Badge>
            )
        },
    },
    {
        key: 'name',
        title: 'Name',
        width: 250,
        pinned: 'left',
        render: (_value, row) => {
            const path =
                row.item_type === ITEM_TYPE.EQUIPMENT
                    ? `${paths.warehouseEquipment}/${row.id}`
                    : `${paths.warehouseComponents}/${row.id}`
            return (
                <Link
                    to={path}
                    className="text-blue-600 hover:underline font-medium"
                >
                    {row.name}
                </Link>
            )
        },
    },
    {
        key: 'service_tag',
        title: 'Service Tag / Ref',
        width: 180,
        render: (_value, row) => (
            <span className="block break-all text-sm leading-tight">
                {row.service_tag || row.ref || '-'}
            </span>
        ),
    },
    {
        key: 'category',
        title: 'Category',
        width: 150,
        render: (value) => (
            <span className="text-sm capitalize">
                {(value as string) || '-'}
            </span>
        ),
    },
    {
        key: 'status',
        title: 'Status',
        width: 140,
        render: (_value, row) => {
            const variant =
                (EQUIPMENT_STATUS_VARIANTS[row.status] as
                    | 'outline'
                    | 'secondary'
                    | 'default'
                    | 'destructive') || 'outline'
            return (
                <Badge variant={variant} className="capitalize">
                    {row.status?.replace('_', ' ')}
                </Badge>
            )
        },
    },
    {
        key: 'condition',
        title: 'Condition',
        width: 130,
        render: (_value, row) => {
            const variant =
                (ITEM_CONDITION_VARIANTS[row.condition] as
                    | 'outline'
                    | 'secondary'
                    | 'default'
                    | 'destructive') || 'outline'
            return (
                <Badge variant={variant} className="capitalize">
                    {row.condition}
                </Badge>
            )
        },
    },
    {
        key: 'current_location',
        title: 'Location',
        width: 200,
        render: (_value, row) => (
            <span className="text-sm">
                {row.current_location?.name || 'N/A'}
            </span>
        ),
    },
    {
        key: 'supplier',
        title: 'Supplier',
        width: 200,
        render: (_value, row) => (
            <span className="text-sm">{row.supplier?.name || '-'}</span>
        ),
    },
    {
        key: 'owner',
        title: 'Owner',
        width: 150,
        render: (value) => (
            <span className="text-sm">{(value as string) || '-'}</span>
        ),
    },
    {
        key: 'created_at',
        title: 'Created',
        width: 150,
        render: (value) => {
            const date = dayjs(value as string)
            return (
                <div
                    className="flex items-center gap-2"
                    title={date.format('YYYY-MM-DD HH:mm:ss')}
                >
                    <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-sm">{date.fromNow()}</span>
                </div>
            )
        },
    },
]

// === Filters ===

const itemTypeOptions = [
    { value: 'equipment', label: 'Equipment' },
    { value: 'component', label: 'Component' },
]

const itemStatusOptions = [
    { value: 'available', label: 'Available' },
    { value: 'in_use', label: 'In Use' },
    { value: 'loaned', label: 'Loaned' },
    { value: 'installed', label: 'Installed' },
    { value: 'sold', label: 'Sold' },
    { value: 'damaged', label: 'Damaged' },
    { value: 'maintenance', label: 'Maintenance' },
]

const itemConditionOptions = [
    { value: 'new', label: 'New' },
    { value: 'used', label: 'Used' },
    { value: 'refurbished', label: 'Refurbished' },
    { value: 'damaged', label: 'Damaged' },
]

export const getItemFilterItems = (
    locationId?: string,
    category?: string
): FilterItemConfig[] => [
    {
        type: 'select',
        name: 'item_type',
        label: 'Type',
        placeholder: 'Filter by type',
        options: itemTypeOptions,
    },
    {
        type: 'select',
        name: 'status',
        label: 'Status',
        placeholder: 'Filter by status',
        options: itemStatusOptions,
    },
    {
        type: 'select',
        name: 'condition',
        label: 'Condition',
        placeholder: 'Filter by condition',
        options: itemConditionOptions,
    },
    {
        type: 'async-select',
        name: 'owner',
        label: 'Owner',
        placeholder: 'Filter by owner',
        asyncOptions: async () => {
            const owners = await getOwners()
            return owners.map((o) => ({ value: o.value, label: o.label }))
        },
        queryKey: 'warehouse-owners',
    },
    {
        type: 'async-select',
        name: 'location_id',
        label: 'Location',
        placeholder: 'Filter by location',
        asyncOptions: async (query) => {
            const res = await getLocations({ q: query, page_size: 50 })
            return (res.results || []).map((l) => ({
                value: l.id,
                label: `${l.name} (${l.type})`,
            }))
        },
        queryKey: 'warehouse-locations',
    },
    {
        type: 'async-select',
        name: 'rack_id',
        label: 'Rack',
        placeholder: 'Filter by rack',
        enabled: !!locationId,
        asyncOptions: async (query) => {
            const res = await getRacks({
                location_id: locationId,
                q: query,
                page_size: 50,
            })
            return (res.results || []).map((r) => ({
                value: r.id,
                label: r.code || r.name,
            }))
        },
        queryKey: `warehouse-racks-${locationId}`,
    },
    {
        type: 'async-select',
        name: 'supplier_id',
        label: 'Supplier',
        placeholder: 'Filter by supplier',
        asyncOptions: async (query) => {
            const res = await getSuppliers({ q: query, page_size: 50 })
            return (res.results || []).map((s) => ({
                value: s.id,
                label: s.name,
            }))
        },
        queryKey: 'warehouse-suppliers',
    },
    {
        type: 'async-select',
        name: 'model_id',
        label: 'Model',
        placeholder: 'Filter by model',
        asyncOptions: async (query) => {
            const res = await getItemModelsPaginated({
                category,
                search: query,
                page_size: 50,
            })
            return (res.results || []).map((m) => ({
                value: m.id,
                label: m.name,
            }))
        },
        queryKey: `warehouse-item-models-${category}`,
    },
]

// === Filter Toggles (column visibility) ===

export const getItemFilterToggles = (): FilterToggle[] => [
    { key: 'item_type', label: 'Type' },
    { key: 'name', label: 'Name' },
    { key: 'service_tag', label: 'Service Tag / Ref' },
    { key: 'category', label: 'Category' },
    { key: 'status', label: 'Status' },
    { key: 'condition', label: 'Condition' },
    { key: 'current_location', label: 'Location' },
    { key: 'supplier', label: 'Supplier' },
    { key: 'owner', label: 'Owner' },
    { key: 'created_at', label: 'Created' },
]
